import { useEffect } from "react"
import { create } from "zustand"

/**
 * What the current page is about, for favorites: `BreadcrumbWrapper` publishes its crumbs (joined by
 * `CRUMB_SEPARATOR`), `ModuleTabs` the active tab and `useRecordRecentlyViewed` the entity's exact
 * name. Clears are owner-checked so a mount/unmount overlap during navigation can't wipe the next
 * page's value.
 */
export const CRUMB_SEPARATOR = " / "

/**
 * The page's own title: the entity's exact name when the page records one (e.g. a variant, which the
 * breadcrumb shows only as its product), else the last crumb, skipping one that just names the tab.
 */
export function pageTitleFrom({
  breadcrumb,
  tabLabel,
  entityLabel,
}: Omit<NavContext, "entitySubtitle">) {
  if (entityLabel) return entityLabel
  const crumbs = breadcrumb?.split(CRUMB_SEPARATOR) ?? []
  if (crumbs.length > 1 && crumbs.at(-1) === tabLabel) crumbs.pop()
  return crumbs.at(-1)
}

type NavContextKey = "breadcrumb" | "tabLabel" | "entityLabel" | "entitySubtitle"
type NavContext = Record<NavContextKey, string | null>

type NavContextStore = NavContext & {
  set: (key: NavContextKey, label: string) => void
  clear: (key: NavContextKey, label: string) => void
}

export const useNavContextStore = create<NavContextStore>((set) => ({
  breadcrumb: null,
  tabLabel: null,
  entityLabel: null,
  entitySubtitle: null,
  set: (key, label) => set({ [key]: label }),
  clear: (key, label) => set((state) => (state[key] === label ? { [key]: null } : state)),
}))

/** Publishes `label` under `key` while mounted. Pass `undefined` while loading. */
export function usePublishNavContext(key: NavContextKey, label: string | undefined) {
  const setLabel = useNavContextStore((s) => s.set)
  const clearLabel = useNavContextStore((s) => s.clear)

  useEffect(() => {
    if (!label) return
    setLabel(key, label)
    return () => clearLabel(key, label)
  }, [key, label, setLabel, clearLabel])
}
